import { GenuinePartTable } from '@/components/sections/service/part-detail/genuine-part';
import { Button } from '@/components/shared/buttons/button';
import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { IPartDetail } from '@/types/shared/parts';

interface PartsDetailProps {
  detail: IPartDetail;
  dataTrack?: string;
  dataTest?: string;
  dataTestSection?: string;
}

export const DescriptionTable = ({
  detail,
  dataTrack = 'parts-detail-cta',
  dataTest = 'partDetail',
  dataTestSection = 'section_description_table',
}: PartsDetailProps) => {
  if (!detail) return null;

  const logoFakePart: ImageProps = {
    src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt64b92ff9d11f16bf/65e81ef51ab2dc53fb979e86/logo-fake-part.svg',
    alt: 'logo fake part',
    width: 214,
    height: 74,
    priority: true,
  };

  return (
    <Section test={dataTestSection}>
      <Container size="desktop-only">
        <Box
          position="relative"
          display="grid"
          alignItems="start"
          rowGap="8"
          columnGap="6"
          marginTop={{ '@lg': '8' }}
          css={{ '@lg': { gridTemplateColumns: '1fr 1fr' } }}
        >
          {/* Image Section */}
          {!!detail.itemImage?.url && (
            <Box
              position="relative"
              display="flex"
              direction="column"
              alignItems="center"
              // height={
              //   !!detail.isImageFullHeight ? { '@lg': 'full' } : undefined
              // }
            >
              <Box
                position="relative"
                width="full"
                height="full"
                test={{
                  dataTest: `${dataTest}_item_image`,
                }}
                // css={{
                //   aspectRatio: '1 / 1',
                //   maxWidth: 604,
                //   maxHeight: 604,
                //   minHeight: 312,

                //   ...(detail.isImageFullHeight
                //     ? {
                //         '@lg': {
                //           aspectRatio: 'auto',
                //           maxHeight: 'none',
                //         },
                //       }
                //     : {
                //         '@lg': { aspectRatio: '4 / 5', maxHeight: 384 },
                //       }),
                // }}
                css={{
                  aspectRatio: '1 / 1',
                  maxWidth: 604,
                  maxHeight: 604,
                  minHeight: 312,
                }}
              >
                <Image
                  priority
                  src={detail.itemImage.url}
                  alt={detail.title}
                  width={400}
                  height={500}
                  layout="fill"
                  aspectRatio="4by5"
                  objectFit="contain"
                  srcSetOptions={{ crop: '4:5' }}
                  useSrcSetPattern
                  sizes="(max-width: 479px) 320px, 640px"
                />
              </Box>
              <Box position="absolute" left="0" bottom="0" width="full">
                <Container size="mobile-only">
                  <Box
                    css={{
                      width: `${(176 / 604) * 100}%`,
                      maxWidth: '176px',
                    }}
                  >
                    <Image
                      priority={logoFakePart.priority}
                      src={logoFakePart.src}
                      alt={logoFakePart.alt}
                      width={logoFakePart.width}
                      height={logoFakePart.height}
                      layout="responsive"
                      useSrcSetPattern
                    />
                  </Box>
                </Container>
              </Box>
            </Box>
          )}
          {/* Detail Section */}
          <Container size="mobile-only">
            <Box display="grid" alignContent="start" rowGap="4">
              {!!detail.title && (
                <Text as="h1" size="text-h2" data-test={`${dataTest}_title`}>
                  {detail.title}
                </Text>
              )}
              {!!detail.partDescription && (
                <Text
                  as="div"
                  size="text-base"
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: detail.partDescription,
                  }}
                  data-test={`${dataTest}_description`}
                />
              )}
              {!!detail.genuineParts?.length && (
                <GenuinePartTable
                  categoryUid={detail.category?.uid || ''}
                  genuineParts={detail.genuineParts}
                  dataTest={dataTest}
                  dataTestSection={dataTestSection}
                  dataTrack={dataTrack}
                />
              )}
              {!!detail.replacementInterval && (
                <Text
                  as="div"
                  size="text-base"
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: `${detail.replacementInterval}`,
                  }}
                  data-test={`${dataTest}_repalcement_interval`}
                />
              )}
              {!!detail.remark && (
                <Text
                  as="div"
                  size="text-base"
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: `<strong>หมายเหตุ: </strong> ${detail.remark}`,
                  }}
                  data-test={`${dataTest}_remark`}
                />
              )}

              {/* Button Group */}
              <ButtonGroup>
                {!!detail.showDealerButton && !!detail.dealerButton && (
                  <Button
                    label={detail.dealerButton.label}
                    href={detail.dealerButton.url}
                    target={detail.dealerButton.target}
                    isMinWidth
                    tracking={{
                      dataTrack: dataTrack,
                      dataTrackSection: 'parts-detail',
                      dataTrackText: detail.dealerButton.label,
                      dataTrackUrl: detail.dealerButton.url,
                    }}
                    test={{
                      dataTest: `${dataTest}_dealer_button`,
                    }}
                  />
                )}
                {!!detail.maintenancesButton && (
                  <Button
                    label={detail.maintenancesButton.label}
                    href={detail.maintenancesButton.url}
                    target={detail.maintenancesButton.target}
                    border
                    isMinWidth
                    tracking={{
                      dataTrack: dataTrack,
                      dataTrackSection: 'parts-detail',
                      dataTrackText: detail.maintenancesButton.label,
                      dataTrackUrl: detail.maintenancesButton.url,
                    }}
                    test={{
                      dataTest: `${dataTest}_maintenances_button`,
                    }}
                  />
                )}
                {!detail.maintenancesButton && !!detail.partItemFile?.src && (
                  <Button
                    label="เช็กราคาเพิ่มเติม"
                    href={detail.partItemFile.src}
                    target="_blank"
                    border={!!detail.showDealerButton}
                    isMinWidth
                    tracking={{
                      dataTrack: dataTrack,
                      dataTrackSection: 'parts-detail',
                      dataTrackText: 'เช็กราคาเพิ่มเติม',
                      dataTrackUrl: detail.partItemFile.src,
                    }}
                    test={{
                      dataTest: `${dataTest}_part_pdf_button`,
                    }}
                  />
                )}
              </ButtonGroup>

              {/* Detail Image */}
              {!!detail.detailImage?.url && (
                <Box
                  marginTop="4"
                  test={{ dataTest: `${dataTest}_detail_image` }}
                >
                  <Image
                    src={detail.detailImage.url}
                    alt={detail.title}
                    width={1600}
                    height={900}
                    layout="responsive"
                    aspectRatio="16by9"
                    useSrcSetPattern
                    sizes="(max-width: 479px) 100vw, 640px"
                  />
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </Container>
    </Section>
  );
};
DescriptionTable.displayName = 'DescriptionTable';
